import { useMemo, useState } from 'react';
import { ChartLine } from 'lucide-react';
import { views } from '@/copy';
import { Button } from '@/ui/design-system/components';
import MultiPredictionTimeSeriesChart from 'components/DataVis/Charts/MultiPredictionTimeSeriesChart';
import ListLegendDropdown from './ListLegendDropdown';
import AssistantVisualFrame from '../primitives/AssistantVisualFrame';
import InlineTableHeader from '../primitives/InlineTableHeader';
import MetricTile from '../primitives/MetricTile';
import {
    CHART_HEIGHT,
    PlainChartWrapper,
    STROKE_WIDTH,
    buildListChrome,
    buildListSeries,
    type ListChartSpec,
} from './entityListModel';
import type { RawRow, VisualProps } from '../types';

function buildEntityListChartModel({
    input,
    output,
    spec,
    customTitle,
}: VisualProps & { spec: ListChartSpec; customTitle?: string }) {
    const chrome = buildListChrome({ input, output, spec, customTitle });
    return {
        ...chrome,
        series: buildListSeries(chrome.rows, spec),
        tiles: spec.tiles(output?._aggregate),
    };
}

const EntityListTimeSeriesView = ({
    input,
    output,
    spec,
    Frame = AssistantVisualFrame,
    title,
    onToggleAggregate,
}: VisualProps & { spec: ListChartSpec }) => {
    const model = useMemo(
        () =>
            buildEntityListChartModel({
                input,
                output,
                spec,
                customTitle: title,
            }),
        [input, output, spec, title],
    );
    // Transient, local state only: hidden lines never reach the persisted
    // config, the data hash, synthesis, or the deck.
    const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => new Set());

    const toggleSeries = (id: string, visibleNext: boolean) =>
        setHiddenIds((prev) => {
            const next = new Set(prev);
            if (visibleNext) next.delete(id);
            else next.add(id);
            return next;
        });

    const { visible, tiles } = useMemo(() => {
        const shown = model.series.filter((s) => !hiddenIds.has(s.id));
        // Every line visible: the backend snapshot is authoritative.
        if (shown.length === model.series.length) {
            return { visible: shown, tiles: model.tiles };
        }
        if (shown.length === 0) {
            return { visible: shown, tiles: spec.tiles(null) };
        }
        const rows = shown.flatMap((s): RawRow[] => (s.row ? [s.row] : []));
        return {
            visible: shown,
            tiles: spec.tiles(spec.aggregateRows?.(rows) ?? null),
        };
    }, [model, hiddenIds, spec]);

    const hasSeries = model.series.length > 0;
    const nothingVisible = visible.length === 0;

    return (
        <Frame breadcrumb={model.breadcrumb}>
            <div className="bg-card border border-border rounded-xl shadow-xs overflow-hidden">
                <InlineTableHeader title={model.title} link={model.link} />
                {model.rows.length === 0 ? (
                    <div className="px-3.5 py-4 text-xs text-muted-foreground text-center">
                        {views.assistantVisuals.noRows}
                    </div>
                ) : (
                    <div className="flex flex-col gap-3 p-4">
                        {hasSeries && (
                            <div className="flex items-center justify-between gap-2">
                                {onToggleAggregate ? (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => onToggleAggregate(true)}
                                    >
                                        <ChartLine />
                                        View combined
                                    </Button>
                                ) : (
                                    <span />
                                )}
                                <ListLegendDropdown
                                    series={model.series}
                                    hiddenIds={hiddenIds}
                                    onToggle={toggleSeries}
                                    preserveNameCase={spec.preserveNameCase}
                                />
                            </div>
                        )}
                        {hasSeries ? (
                            <MultiPredictionTimeSeriesChart
                                chartHeight={CHART_HEIGHT}
                                data={visible}
                                showPlaceholder={nothingVisible}
                                placeholderMessage={
                                    views.assistantVisuals.noLinesSelected
                                }
                                Tooltip={spec.Tooltip}
                                Wrapper={PlainChartWrapper}
                                showAxes={false}
                                strokeWidth={STROKE_WIDTH}
                            />
                        ) : (
                            <div
                                className="flex items-center justify-center rounded-md bg-muted text-xs text-muted-foreground"
                                style={{ height: CHART_HEIGHT }}
                            >
                                {views.assistantVisuals.noTrendLines}
                            </div>
                        )}
                        <div className="grid grid-cols-3 gap-2">
                            {tiles.map((tile) => (
                                <MetricTile
                                    compact
                                    key={tile.label}
                                    {...tile}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Frame>
    );
};

export default EntityListTimeSeriesView;
